import React, { useState } from "react";

const BACKEND_URL = "https://reflex-backend.alfaoir1.workers.dev";

export default function HiringApply() {
  const [formData, setFormData] = useState({
    fullName: "", email: "", phone: "", address: "",
    salary: "", availability: "", internetSpeed: "",
    laptopModel: "", headphoneModel: "",
  });
  const [cv, setCv] = useState(null);
  const [idCard, setIdCard] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handle = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("fileName", file.name);
    const res = await fetch(`${BACKEND_URL}/upload`, { method: "POST", body: fd });
    const data = await res.json();
    if (!data.success) throw new Error("File upload failed");
    return data.fileUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let cvKey = null;
      let idCardKey = null;
      if (cv) cvKey = await uploadFile(cv);
      if (idCard) idCardKey = await uploadFile(idCard);
      const response = await fetch(`${BACKEND_URL}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, cvKey, idCardKey }),
      });
      const data = await response.json();
      if (data.success) setSubmitted(true);
      else setError("Something went wrong. Please try again.");
    } catch (err) {
      setError("Failed to submit. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={styles.page}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=SF+Pro+Display:wght@300;400;600&family=Inter:wght@300;400;500;600&display=swap');
          @keyframes backdropIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes cardIn {
            from { opacity: 0; transform: scale(0.92) translateY(32px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes checkIn {
            from { opacity: 0; transform: scale(0.5); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes ringPulse {
            0% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.35); opacity: 0; }
            100% { transform: scale(1); opacity: 0; }
          }
          @keyveis fadeUp {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .backdrop { animation: backdropIn 0.4s ease forwards; }
          .success-card { animation: cardIn 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards; }
          .check-icon { animation: checkIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.3s both; }
          .ring-pulse {
            position: absolute; inset: -10px;
            border-radius: 50%;
            border: 1.5px solid rgba(168,85,247,0.6);
            animation: ringPulse 2s ease-out 0.5s infinite;
          }
          .ring-pulse-2 {
            position: absolute; inset: -10px;
            border-radius: 50%;
            border: 1.5px solid rgba(168,85,247,0.4);
            animation: ringPulse 2s ease-out 0.9s infinite;
          }
          .text-1 { animation: fadeUp 0.6s ease 0.45s both; }
          .text-2 { animation: fadeUp 0.6s ease 0.6s both; }
          .text-3 { animation: fadeUp 0.6s ease 0.75s both; }
          .text-4 { animation: fadeUp 0.6s ease 0.9s both; }
        `}</style>

        <audio autoPlay loop>
          <source src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/audios/cornfield_chase.mp3" type="audio/mpeg" />
        </audio>

        {/* Full screen backdrop */}
        <div className="backdrop" style={styles.successBackdrop}>
          <div style={styles.noiseOverlay} />
          <div style={styles.glowTop} />
          <div style={styles.glowBottom} />

          {/* Card */}
          <div className="success-card" style={styles.successCard}>

            {/* Logo */}
            <div className="text-1" style={styles.logoWrap}>
              <img
                src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/reflex.png"
                alt="Reflex"
                style={styles.logo}
              />
            </div>

            {/* Check icon */}
            <div style={styles.checkWrap}>
              <div className="ring-pulse" />
              <div className="ring-pulse-2" />
              <div className="check-icon" style={styles.checkIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Heading */}
            <h1 className="text-2" style={styles.successTitle}>Application Received</h1>

            {/* Divider */}
            <div className="text-2" style={styles.successDivider} />

            {/* Body */}
            <p className="text-3" style={styles.successPara}>
              Thank you for taking this step with us.
            </p>
            <p className="text-3" style={styles.successParaMain}>
              We're building more than a company — we're building a family united by purpose. If you join us, you become part of something bigger: creating impact, preserving integrity, and bringing more humanity into everything we do.
            </p>

            {/* Footer */}
            <p className="text-4" style={styles.successFooter}>
              We'll review your application and be in touch shortly.
            </p>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        .form-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 13px 16px;
          font-size: 15px;
          color: #fff;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: border-color 0.2s ease;
        }
        .form-input:focus { border-color: rgba(123,25,188,0.6); }
        .form-input::placeholder { color: #4b5563; }
        .upload-box {
          width: 100%;
          background: rgba(255,255,255,0.02);
          border: 1px dashed rgba(255,255,255,0.15);
          border-radius: 10px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.2s ease;
          position: relative;
        }
        .upload-box:hover { border-color: rgba(123,25,188,0.5); }
        .upload-box input[type="file"] {
          position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;
        }
        .submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #7b19bc, #9f3ee5);
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 16px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          transition: filter 0.2s ease, transform 0.2s ease;
        }
        .submit-btn:hover { filter: brightness(1.15); transform: translateY(-2px); }
        .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        @media (max-width: 640px) {
          .form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={styles.hero}>
        <p style={styles.heroLabel}>Join Our Team</p>
        <h1 style={styles.heroTitle}>Apply Now</h1>
        <p style={styles.heroSub}>Fill out the form below and we'll be in touch shortly.</p>
      </div>

      <div style={styles.formWrapper}>
        <form onSubmit={handleSubmit} style={styles.form}>

          <p style={styles.groupLabel}>Personal Information</p>
          <div style={styles.grid} className="form-grid">
            <div style={styles.field}>
              <label style={styles.label}>Full Name *</label>
              <input className="form-input" name="fullName" placeholder="Your full name"
                value={formData.fullName} onChange={handle} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email Address *</label>
              <input className="form-input" type="email" name="email" placeholder="you@example.com"
                value={formData.email} onChange={handle} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Phone Number *</label>
              <input className="form-input" name="phone" placeholder="+1 234 567 8900"
                value={formData.phone} onChange={handle} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Expected Salary</label>
              <input className="form-input" name="salary" placeholder="e.g. $500/month"
                value={formData.salary} onChange={handle} />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Address</label>
            <textarea className="form-input" name="address" placeholder="Your full address"
              rows={3} value={formData.address} onChange={handle} style={{ resize: "vertical" }} />
          </div>

          <p style={styles.groupLabel}>Availability & Setup</p>
          <div style={styles.grid} className="form-grid">
            <div style={styles.field}>
              <label style={styles.label}>Availability / Start Date *</label>
              <input className="form-input" name="availability" placeholder="e.g. Immediately / June 1"
                value={formData.availability} onChange={handle} required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Internet Speed</label>
              <input className="form-input" name="internetSpeed" placeholder="e.g. 50 Mbps"
                value={formData.internetSpeed} onChange={handle} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Laptop Name / Model</label>
              <input className="form-input" name="laptopModel" placeholder="e.g. Dell Inspiron 15"
                value={formData.laptopModel} onChange={handle} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Headphone Name / Model</label>
              <input className="form-input" name="headphoneModel" placeholder="e.g. Sony WH-1000XM4"
                value={formData.headphoneModel} onChange={handle} />
            </div>
          </div>

          <p style={styles.groupLabel}>Documents</p>
          <div style={styles.grid} className="form-grid">
            <div style={styles.field}>
              <label style={styles.label}>Upload CV</label>
              <div className="upload-box">
                <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCv(e.target.files[0])} />
                <div style={styles.uploadIcon}>📄</div>
                <p style={styles.uploadText}>{cv ? cv.name : "Click to upload CV"}</p>
                <p style={styles.uploadHint}>PDF or DOC accepted</p>
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Upload Identity Card</label>
              <div className="upload-box">
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setIdCard(e.target.files[0])} />
                <div style={styles.uploadIcon}>🪪</div>
                <p style={styles.uploadText}>{idCard ? idCard.name : "Click to upload ID"}</p>
                <p style={styles.uploadHint}>Image or PDF accepted</p>
              </div>
            </div>
          </div>

          {error && <p style={styles.errorText}>{error}</p>}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "Uploading & Submitting..." : "Submit Application →"}
          </button>

        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#0d1117",
    color: "#fff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    minHeight: "100vh",
  },
  hero: {
    background: "linear-gradient(160deg, #0a0d14 0%, #121723 60%, #1a0a2e 100%)",
    padding: "120px 20px 70px",
    textAlign: "center",
    borderBottom: "1px solid rgba(123,25,188,0.2)",
  },
  heroLabel: { fontSize: "12px", color: "#9f3ee5", textTransform: "uppercase", letterSpacing: "3px", marginBottom: "16px" },
  heroTitle: { fontSize: "48px", fontWeight: 800, margin: "0 0 14px", color: "#fff", letterSpacing: "-0.03em" },
  heroSub: { fontSize: "17px", color: "#a0a6b5" },
  formWrapper: { maxWidth: "760px", margin: "0 auto", padding: "60px 20px 80px" },
  form: { display: "flex", flexDirection: "column", gap: "20px" },
  groupLabel: { fontSize: "12px", fontWeight: 700, color: "#7b19bc", textTransform: "uppercase", letterSpacing: "2px", margin: "10px 0 0" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" },
  field: { display: "flex", flexDirection: "column", gap: "8px" },
  label: { fontSize: "13px", color: "#9ca3af", fontWeight: 500 },
  uploadIcon: { fontSize: "28px", marginBottom: "8px" },
  uploadText: { fontSize: "14px", color: "#e2e8f0", margin: "0 0 4px" },
  uploadHint: { fontSize: "12px", color: "#4b5563", margin: 0 },
  errorText: {
    color: "#ef4444", fontSize: "14px",
    background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)",
    borderRadius: "8px", padding: "12px 16px",
  },

  // ── Success screen ──────────────────────────────────────
  successBackdrop: {
    position: "fixed", inset: 0,
    background: "linear-gradient(135deg, #0a0010 0%, #0d0118 40%, #080012 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "20px",
    zIndex: 999,
    overflow: "hidden",
  },
  noiseOverlay: {
    position: "absolute", inset: 0,
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E\")",
    opacity: 0.4, pointerEvents: "none",
  },
  glowTop: {
    position: "absolute", top: "-200px", left: "50%",
    transform: "translateX(-50%)",
    width: "800px", height: "600px",
    background: "radial-gradient(ellipse, rgba(139,31,210,0.35) 0%, transparent 65%)",
    pointerEvents: "none",
  },
  glowBottom: {
    position: "absolute", bottom: "-200px", left: "50%",
    transform: "translateX(-50%)",
    width: "600px", height: "500px",
    background: "radial-gradient(ellipse, rgba(88,12,160,0.2) 0%, transparent 65%)",
    pointerEvents: "none",
  },
  successCard: {
    position: "relative", zIndex: 1,
    width: "100%", maxWidth: "480px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "28px",
    padding: "52px 44px",
    textAlign: "center",
    backdropFilter: "blur(40px)",
    boxShadow: "0 0 0 1px rgba(168,85,247,0.1), 0 40px 80px rgba(0,0,0,0.6), 0 0 80px rgba(139,31,210,0.15)",
  },
  logoWrap: { marginBottom: "36px" },
  logo: { height: "36px", objectFit: "contain", opacity: 0.9 },
  checkWrap: {
    position: "relative",
    width: "72px", height: "72px",
    margin: "0 auto 32px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  checkIcon: {
    width: "72px", height: "72px",
    background: "linear-gradient(135deg, #7b19bc, #a855f7)",
    borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 0 40px rgba(168,85,247,0.5)",
  },
  successTitle: {
    fontSize: "28px",
    fontWeight: 600,
    color: "#ffffff",
    margin: "0 0 20px",
    letterSpacing: "-0.02em",
    fontFamily: "-apple-system, 'SF Pro Display', BlinkMacSystemFont, sans-serif",
  },
  successDivider: {
    width: "36px", height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(168,85,247,0.7), transparent)",
    margin: "0 auto 24px",
  },
  successPara: {
    fontSize: "17px",
    fontWeight: 400,
    color: "#ffffff",
    margin: "0 0 14px",
    lineHeight: 1.6,
    fontFamily: "-apple-system, 'SF Pro Display', BlinkMacSystemFont, sans-serif",
    letterSpacing: "-0.01em",
  },
  successParaMain: {
    fontSize: "15px",
    fontWeight: 300,
    color: "#ffffff",
    lineHeight: 1.75,
    margin: "0 0 36px",
    fontFamily: "-apple-system, 'SF Pro Display', BlinkMacSystemFont, sans-serif",
    letterSpacing: "-0.005em",
    opacity: 0.85,
  },
  successFooter: {
    fontSize: "12px",
    color: "rgba(255,255,255,0.35)",
    margin: 0,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
  },
};